import {
  CancelOutlined,
  CheckCircleOutline,
  HelpOutline
} from '@mui/icons-material'
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  SxProps,
  Theme
} from '@mui/material'
import ButtonBase from 'design-system/button'
import { useState } from 'react'

interface DisagreeButtonProps {
  isDisagreed?: boolean
  onClick: () => Promise<void> | void
  disabled?: boolean
  loading?: boolean
  onSuccess?: () => void
  sx?: SxProps<Theme>
}

const FEEDBACK_TOOLTIP_TEXT =
  'Ao discordar de uma análise você participa ativamente da melhoria do modelo de análise'

export const DisagreeButton: React.FC<DisagreeButtonProps> = ({
  isDisagreed = false,
  onClick,
  disabled = false,
  loading = false,
  onSuccess,
  sx
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const theme = useTheme()

  const handleClick = async (e?: React.MouseEvent) => {
    // Prevent event propagation to parent elements (especially modal backdrop)
    e?.preventDefault()
    e?.stopPropagation()

    setIsProcessing(true)
    try {
      await onClick()
      // Only call onSuccess after API confirms the feedback was sent
      onSuccess?.()
    } catch (error) {
      console.error('Error in DisagreeButton:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const isDisabled = disabled || loading || isProcessing

  return (
    <Box display='flex' alignItems='center' gap={0.5}>
      <ButtonBase
        variant='contained'
        color={isDisagreed ? 'primary' : 'warning'}
        size='small'
        startIcon={isDisagreed ? <CheckCircleOutline /> : <CancelOutlined />}
        onClick={handleClick}
        disabled={isDisabled}
        loading={loading || isProcessing}
        sx={
          sx || {
            width: '110px',
            padding: '5px'
          }
        }
      >
        {isDisagreed ? 'Concordar' : 'Discordar'}
      </ButtonBase>

      <Tooltip
        title={FEEDBACK_TOOLTIP_TEXT}
        placement='right'
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                maxWidth: 300,
                padding: '8px 12px',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontSize: '0.875rem',
                borderRadius: '4px'
              }
            }
          }
        }}
      >
        <IconButton
          size='small'
          sx={{
            color: 'primary.main',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <HelpOutline sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}
